/*
 * lp-elevar
 * ------------------------------------------------------------------
 * Supplements Elevar's data layer on the LP (a custom template Elevar can't
 * auto-classify). Reads the product data emitted by snippets/lp-elevar-datalayer
 * and pushes, to window.ElevarDataLayer:
 *   dl_view_item_list - all shades as impressions (the "collection")
 *   dl_view_item      - the first shade as the "PDP" view
 *   dl_select_item    - on selecting a shade (via the cart's lp-cart:change)
 *
 * user_properties are reused from the dl_user_data event Elevar's app already
 * fires (device/page/marketing are auto-added by Elevar). We wait for that base
 * event before firing, since Elevar requires it first.
 *
 * We never set an event_id: Elevar's listener generates one per event and reuses it across the
 * web + server sends (that's how those two get deduped downstream). It does NOT dedupe OUR own
 * duplicates, so the window.__lpElevarInit guard below fires these events at most once per page.
 * Also: don't add a manual dl_add_to_cart here — Elevar auto-fires it on cart/add.js globally, and
 * a manual one would carry a different event_id and double-count. (See CLAUDE.md → Analytics.)
 */
(() => {
  // NOTE: this script is only loaded on pages allowlisted in snippets/elevar-tracking-enabled
  // (snippets/lp-elevar-datalayer skips both the payload and this <script> tag elsewhere).
  // That allowlist is the single on/off switch — don't add a second one here.

  // Initialise the LP's Elevar supplementation at most ONCE per page. Guards against
  // duplicate reporting (both the view events and the select_item listener) if this script is
  // ever included by more than one section on the same page — each <script> tag executes even
  // when the URL is already cached. Also makes the "fire view events exactly once" guarantee
  // explicit rather than incidental to the poll's control flow.
  if (window.__lpElevarInit) return;
  window.__lpElevarInit = true;

  const cfgEl = document.querySelector('[data-lp-elevar]');
  if (!cfgEl) return;

  let cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }
  // One shared array of product objects, re-wrapped per event below.
  if (!cfg.products || !cfg.products.length) return;

  window.ElevarDataLayer = window.ElevarDataLayer || [];
  const push = (obj) => window.ElevarDataLayer.push(obj);

  // Reuse user_properties from the base dl_user_data event (most recent).
  const userProps = () => {
    const dl = window.ElevarDataLayer;
    for (let i = dl.length - 1; i >= 0; i--) {
      if (dl[i] && dl[i].event === 'dl_user_data' && dl[i].user_properties) return dl[i].user_properties;
    }
    return { visitor_type: 'guest' };
  };

  // The view_item product is chosen on the Landing page settings section (emitted as
  // [data-lp-view-item]); fall back to the first shade if unset or not in the list.
  let viewItemId = '';
  const viewItemEl = document.querySelector('[data-lp-view-item]');
  if (viewItemEl) {
    try {
      viewItemId = (JSON.parse(viewItemEl.textContent) || {}).viewItemId || '';
    } catch (e) {
      /* ignore */
    }
  }
  const viewItem =
    (viewItemId && cfg.products.find((p) => p.product_id === viewItemId)) || cfg.products[0];

  const fireViewEvents = () => {
    const up = userProps();
    push({
      event: 'dl_view_item_list',
      user_properties: up,
      ecommerce: { currencyCode: cfg.currencyCode, impressions: cfg.products },
    });
    push({
      event: 'dl_view_item',
      user_properties: up,
      ecommerce: {
        currencyCode: cfg.currencyCode,
        detail: {
          actionField: { list: cfg.list, action: 'detail' },
          products: [viewItem],
        },
      },
    });
  };

  // Elevar requires dl_user_data first; wait briefly for it, then fire (cap the wait).
  let tries = 0;
  const waitAndFire = () => {
    const ready = window.ElevarDataLayer.some((e) => e && e.event === 'dl_user_data');
    if (ready || tries > 40) {
      fireViewEvents();
      return;
    }
    tries++;
    setTimeout(waitAndFire, 100);
  };
  waitAndFire();

  // Don't fire select_item for the initially pre-selected shades, which dispatch
  // lp-cart:change synchronously on load; arm only after that cascade settles.
  let selectReady = false;
  setTimeout(() => {
    selectReady = true;
  }, 0);

  // dl_select_item when a shade is selected (not removed). The cart dispatches
  // lp-cart:change with action 'add'|'remove'; default-type lines are the shades.
  document.addEventListener('lp-cart:change', (e) => {
    if (!selectReady) return;
    const d = e.detail || {};
    if (d.action !== 'add' || !d.item || d.item.type !== 'default') return;
    const vid = String(d.item.variantId);
    const product = cfg.products.find((p) => p.variant_id === vid);
    if (!product) return;
    push({
      event: 'dl_select_item',
      user_properties: userProps(),
      ecommerce: {
        currencyCode: cfg.currencyCode,
        click: {
          actionField: { list: cfg.list, action: 'click' },
          products: [product],
        },
      },
    });
  });
})();
